import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Type } from 'lucide-react';
import { useFieldTypeCatalog } from '../../../hooks/useFieldTypeCatalog';
import type { FieldType } from '../../../types/forms';

interface AddFieldModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (type: FieldType) => void;
  onImportBatch?: (labels: string[]) => void;
}

const MAX_IMPORT = 50;

type ModalTab = 'add' | 'import';

/** Parse markdown-like lines into question labels */
function parseQuestions(raw: string): string[] {
  return raw
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0)
    .filter(line => !line.startsWith('#')) // ignore MD headers
    .map(line => {
      // - item or * item
      if (/^[-*]\s+/.test(line)) return line.replace(/^[-*]\s+/, '');
      // N. item (numbered list)
      if (/^\d+\.\s+/.test(line)) return line.replace(/^\d+\.\s+/, '');
      return line;
    })
    .map(l => l.trim())
    .filter(l => l.length > 0);
}

export default function AddFieldModal({ isOpen, onClose, onSelect, onImportBatch }: AddFieldModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<ModalTab>('add');
  const [importText, setImportText] = useState('');
  const { byCategory, categoryLabels, columns } = useFieldTypeCatalog();

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab('add');
      setImportText('');
    }
  }, [isOpen]);

  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Focus trap
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const modal = modalRef.current;
    const focusableSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusable = modal.querySelectorAll<HTMLElement>(focusableSelector);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTab);

    const firstFocusable = modal.querySelector<HTMLElement>(focusableSelector);
    firstFocusable?.focus();

    return () => document.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  const handleSelect = useCallback((type: FieldType) => {
    onSelect(type);
    onClose();
  }, [onSelect, onClose]);

  // Parsed questions from import text
  const parsedQuestions = useMemo(() => parseQuestions(importText), [importText]);
  const isTruncated = parsedQuestions.length > MAX_IMPORT;
  const displayQuestions = isTruncated ? parsedQuestions.slice(0, MAX_IMPORT) : parsedQuestions;

  const handleImport = useCallback(() => {
    if (displayQuestions.length === 0 || !onImportBatch) return;
    onImportBatch(displayQuestions);
    onClose();
  }, [displayQuestions, onImportBatch, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Adicionar campo"
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl mx-4 max-h-[85vh] overflow-y-auto rounded-2xl bg-neutral-900 border border-white/10 shadow-2xl"
      >
        {/* Header with tabs */}
        <div className="sticky top-0 z-10 bg-neutral-900 border-b border-white/10">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-1">
              <button
                onClick={() => setActiveTab('add')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'add'
                    ? 'bg-white/10 text-white'
                    : 'text-neutral-400 hover:text-white hover:bg-white/5'
                }`}
              >
                Adicionar campos
              </button>
              <button
                onClick={() => setActiveTab('import')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'import'
                    ? 'bg-white/10 text-white'
                    : 'text-neutral-400 hover:text-white hover:bg-white/5'
                }`}
              >
                Importar Perguntas
              </button>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-white/10 transition-colors"
              aria-label="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tab: Adicionar campos */}
        {activeTab === 'add' && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-0 sm:gap-6 p-6">
            {columns.map((categoryKeys, colIdx) => (
              <div key={colIdx} className="space-y-5">
                {categoryKeys.map(catKey => {
                  const items = byCategory[catKey];
                  if (!items?.length) return null;
                  return (
                    <div key={catKey}>
                      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 mb-2 px-1">
                        {categoryLabels[catKey]}
                      </h3>
                      <div className="space-y-0.5">
                        {items.map(item => (
                          <button
                            key={item.type}
                            onClick={() => handleSelect(item.type)}
                            className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm text-neutral-300 hover:text-white hover:bg-white/10 transition-colors"
                          >
                            <item.icon className="w-4 h-4 text-neutral-400 shrink-0" />
                            {item.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {/* Tab: Importar Perguntas */}
        {activeTab === 'import' && (
          <div className="p-6 space-y-4">
            <textarea
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder={`Cole suas perguntas (uma por linha):\n- Qual seu nome?\n- Qual seu email?\n1. Como nos conheceu?`}
              className="w-full h-40 rounded-xl bg-neutral-800/60 border border-white/10 text-sm text-neutral-200 placeholder-neutral-500 px-4 py-3 resize-none focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/30 transition-colors"
            />

            {/* Truncation warning */}
            {isTruncated && (
              <div className="text-amber-400 text-xs px-1">
                Máximo de {MAX_IMPORT} perguntas por importação. {parsedQuestions.length - MAX_IMPORT} pergunta(s) ignorada(s).
              </div>
            )}

            {/* Preview */}
            {displayQuestions.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs text-neutral-500 px-1">
                  {displayQuestions.length} pergunta{displayQuestions.length !== 1 ? 's' : ''} encontrada{displayQuestions.length !== 1 ? 's' : ''}:
                </p>
                <div className="max-h-48 overflow-y-auto rounded-xl bg-neutral-800/40 border border-white/5 divide-y divide-white/5">
                  {displayQuestions.map((q, i) => (
                    <div key={i} className="flex items-center gap-2.5 px-3 py-2 text-sm text-neutral-300">
                      <Type className="w-3.5 h-3.5 text-neutral-500 shrink-0" />
                      <span className="truncate">{q}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Import button */}
            <button
              onClick={handleImport}
              disabled={displayQuestions.length === 0}
              className="w-full py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-primary-600 text-white hover:bg-primary-500"
            >
              Importar {displayQuestions.length > 0 ? `${displayQuestions.length} pergunta${displayQuestions.length !== 1 ? 's' : ''}` : ''}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
